/* jshint node: true */
/* eslint-disable no-param-reassign */

const _ = require('underscore');
const { getProperty } = require('./PropertyGetter');
const Properties = require('./Properties');
const { SEO_SHOW_ALL_REVIEWS_OFFSET } = require('../../../server/utils/server-constants');
const {
  andFunctor,
  imagesAdapter,
} = require('./productModelHelper/PatternsUtils');
const {
  checkCategoryEligibility,
  getDeviceType,
  getSizesByType,
} = require('./productModelHelper/ProductModelParserUtil');

const SizesType = {
  letterSize: 'letterSize',
  numericSize: 'numericSize',
};

function isNodeEnv() {
  return typeof window === 'undefined';
}

function isIshipPage({ isIntlShipping = false } = {}) {
  if (isNodeEnv()) {
    return isIntlShipping;
  }
  const countryCode = require('@component/common/src/util/Cookie').getCountryCode();
  return countryCode && countryCode !== 'US';
}

/* eslint-disable-next-line no-useless-escape */
const isRegistryUrlMode = url => /\/(shop|test)\/registry\/wedding\/product\/(([^\/]*?).*ID.+)/gi.test(url);

function isOnRegistryMode(product, options) {
  return product.detail.flags.registrable && (isNodeEnv() ? options.isUrlRegistryMode : isRegistryUrlMode(window.location.href));
}

// eslint-disable-next-line no-unused-vars
function isServerSideExperimentEnabled(exp, options) {
  return options.experimentIds && options.experimentIds.indexOf(exp) > -1;
}

function getBadgeInformation(product, selectedSize) {
  const selectedSizePresent = selectedSize !== undefined;
  if (!product.detail.flags.consolidatedProductComplex || !selectedSizePresent) {
    const isPriceDefined = !(product.pricing === undefined);
    const badgesMap = (isPriceDefined && !(product.pricing.badgesMap === undefined)) ? product.pricing.badgesMap : {};

    if (!isPriceDefined || _.isEmpty(badgesMap)) {
      return [];
    }

    return _.chain(product.pricing.badgeIds)
      .map(badgeId => badgesMap[badgeId])
      .value();
  }

  const isSizeMapDefined = !(product.traits.sizes.sizeMap === undefined);
  const isBadgesMapDefined = !(Object.values(product.traits.sizes.sizeMap)[selectedSize].pricing.badgesMap === undefined);
  const badgesMap = (isSizeMapDefined && isBadgesMapDefined) ? Object.values(product.traits.sizes.sizeMap)[selectedSize].pricing.badgesMap : {};

  if (!isSizeMapDefined || _.isEmpty(badgesMap)) {
    return [];
  }

  return _.chain(product.traits.sizes.sizeMap[`${selectedSize}`].pricing.badgeIds)
    .map(badgeId => badgesMap[badgeId])
    .value();
}

function getFinalPricePosition(topBadges, finalPrice) {
  const topBadgesPromoId = _.pluck(topBadges, 'id');
  const finalPricePromoId = finalPrice.applicablePromotions;

  let finalPricePosition = 0;

  if (topBadgesPromoId.length >= finalPricePromoId.length) {
    finalPricePosition = _.intersection(topBadgesPromoId, finalPricePromoId).length;
  }

  return finalPricePosition;
}

function formatFinalPrice(selectedFinalPrice) {
  if (!(selectedFinalPrice.label === undefined) && selectedFinalPrice.values.length) {
    const value = selectedFinalPrice.values[1] ?
      (selectedFinalPrice.values[0].formattedValue - selectedFinalPrice.values[1].noCurrency)
      : selectedFinalPrice.values[0].formattedValue;
    // eslint-disable-next-line no-param-reassign
    selectedFinalPrice.formattedFinalPrice = selectedFinalPrice.label.replace('[PRICE]', value);
  }
  return selectedFinalPrice;
}

function formatSecondBadge(secondBadgeHeader) {
  return `Plus ${secondBadgeHeader.charAt(0).toLowerCase()}${secondBadgeHeader.slice(1)}`;
}

function getTopBadges(badgeData = [], finalPrice, showFinalPrice, flags, isGiftWithPurchaseItem, colorBadges) {
  const badgeDataSet = (Properties.get('isUpcLevelPromoBadgingEnabled') && !flags.masterProduct && !isGiftWithPurchaseItem && !flags.giftCard && colorBadges.length > 0) ? badgeData : badgeData.slice(0, 2);
  const topBadges = badgeDataSet.map(badge => ({
    header: badge.header,
    applicableToAllUpcs: !(badge.applicableToAllUpcs === undefined),
    id: badge.promoId,
    code: badge.promoCode || '',
    promotionType: badge.promotionType,
    loyaltyEligibleTier: badge.loyaltyEligibleTier,
  }));

  if (!_.isEmpty(finalPrice) && !finalPrice.maskPromotion && showFinalPrice && topBadges.length) {
    const position = getFinalPricePosition(topBadges, finalPrice);
    const secondBadge = (topBadges.length > 1) ? topBadges[1].header : '';
    if (position) {
      if (position === 2 && secondBadge && secondBadge[0] === secondBadge[0].toUpperCase()) {
        topBadges[1].header = formatSecondBadge(secondBadge);
      }
      topBadges.splice(position, 0, finalPrice);
    }
  }
  return topBadges;
}

function getFinalPrice(topBadges, finalPrice) {
  const position = !_.isEmpty(finalPrice) ? getFinalPricePosition(topBadges, finalPrice) : 0;

  if (topBadges.length && position) {
    return finalPrice;
  }

  return undefined;
}

function getUpcId(product, color, size, type) {
  const keyToUpc = color + (_.isNumber(size) ? `_${size}` : '') + (_.isNumber(type) ? `_${type}` : '');
  return product.traits.traitsMaps.upcMap[keyToUpc];
}

function getFirstUpcByColorID(model, colorID) {
  const object = {};
  // eslint-disable-next-line prefer-destructuring
  const upcMap = model.traits.traitsMaps.upcMap;
  const hasTypes = model.traits.typeMap;
  let upcID;

  _.each(upcMap, (val, key) => {
    const colorKey = key.split('_')[0];
    if (typeof hasTypes !== 'undefined') {
      if (parseInt(colorKey, 10) === colorID) {
        upcID = upcMap[key];
      }
    } else if (parseInt(colorKey, 10) === colorID) {
      upcID = upcMap[key];
    }
  });
  if (upcID) {
    object.upcID = upcID;
    object.upcNumber = model.relationships.upcs[upcID].identifier.upcNumber;
  }
  return object;
}

function getUpcAvailable(product, color, size, type) {
  const upcId = getUpcId(product, color, size, type);
  return upcId && (getProperty(
      { product, upcId },
      'product.relationships.upcs[upcId].availability.available', false,
    ) || getProperty(
      { product, upcId },
      'product.relationships.upcs[upcId].availability.bopsAvailability', false,
    ));
}

function getAvailabilityForMember(product, colorId, sizeId, typeId) {
  const availability = {};
  const { traits, detail } = product;
  const { colors } = traits;
  const hasType = traits.types;
  const hasSize = traits.sizes;
  const colorObj = !(colors.colorMap === undefined) && colors.colorMap[colorId];
  const typeObj = _.isNumber(typeId) && traits.types.typeMap[typeId];
  const sizeObj = _.isNumber(sizeId) && traits.sizes.sizeMap[sizeId];

  availability.colors = {};
  if (hasType && typeObj) {
    if (hasSize) {
      if (_.isNumber(sizeId)) {
        _.each(typeObj.sizeToColors[sizeId], (currentColorId) => {
          availability.colors[currentColorId] = getUpcAvailable(product, currentColorId, sizeId, typeId);
        });
      } else {
        _.each(typeObj.colorToSizes, (color, currentColorId) => {
          availability.colors[currentColorId] = true;
        });
      }
    } else {
      _.each(typeObj.colors, (currentColorId) => {
        availability.colors[currentColorId] = getUpcAvailable(product, currentColorId, false, typeId);
      });
    }
  } else if (hasSize && sizeObj) {
    if (hasType) {
      _.each(sizeObj.colorToTypes, (color, currentColorId) => {
        availability.colors[currentColorId] = true;
      });
    } else if (!(colors.colorMap === undefined)) {
      _.each(colors.colorMap, (color, currentColorId) => {
        availability.colors[currentColorId] = getUpcAvailable(product, currentColorId, sizeId, false);
      });
    }
  } else if (hasType || hasSize) {
    _.each(colors.colorMap, (color, currentColorId) => {
      availability.colors[currentColorId] = true;
    });
  } else {
    _.each(colors.colorMap, (color, currentColorId) => {
      availability.colors[currentColorId] = getUpcAvailable(product, currentColorId, false, false);
    });
  }

  if (hasSize) {
    availability.sizes = {};
    if (hasType) {
      if (_.isNumber(typeId)) {
        _.each(colorObj.typeToSizes[typeId], (currentSizeId) => {
          availability.sizes[currentSizeId] = getUpcAvailable(product, colorId, currentSizeId, typeId);
        });
      } else {
        _.each(colorObj.sizeToTypes, (size, currentSizeId) => {
          availability.sizes[currentSizeId] = true;
        });
      }
    } else {
      _.each(colorObj.sizes, (currentSizeId) => {
        availability.sizes[currentSizeId] = getUpcAvailable(product, colorId, currentSizeId, false);
      });
    }
  }
  if (hasType) {
    availability.types = {};
    if (hasSize) {
      if (_.isNumber(sizeId)) {
        _.each(colorObj.sizeToTypes[sizeId], (currentTypeId) => {
          availability.types[currentTypeId] = getUpcAvailable(product, colorId, sizeId, currentTypeId);
        });


        // TODO: Dress selectors clean up
        // If not big ticket
        if (!getProperty(detail, 'flags.bigTicketItem', false)) {
          // For all the types whose availability is not determined yet
          _.each(Object.keys(traits.types.typeMap).filter(k => !k.includes(typeId) || !availability.types[k]), (currentTypeId) => {
            // Get the sizes for the selected color
            const sizes = traits.types.typeMap[currentTypeId].colorToSizes[colorId];
            if (sizes) {
              // If at least one size for this type and selected color is available, mark the availability true for this type
              availability.types[currentTypeId] = sizes
                .some(currentSizeId => getUpcAvailable(product, colorId, currentSizeId, parseInt(currentTypeId, 10)));
            } else {
              availability.types[currentTypeId] = false;
            }
          });
        }
      } else {
        _.each(colorObj.typeToSizes, (size, currentTypeId) => {
          availability.types[currentTypeId] = true;
        });
      }
    } else {
      _.each(colorObj.types, (currentTypeId) => {
        availability.types[currentTypeId] = getUpcAvailable(product, colorId, false, currentTypeId);
      });
    }
  }
  return availability;
}

function buildDropDownColorData(product) {
  const { traits } = product;
  const { colors } = traits;
  const { colorMap } = colors;
  const swatchSpriteUrls = getProperty(colors, 'swatchSprite.swatchSpriteUrls', {});
  const dropdowncolor = {};

  _.each(colors.colorMap, (color, currentColorId) => {
    const swatchImage = colorMap[currentColorId].swatchImage || {};

    dropdowncolor[currentColorId] = {
      id: currentColorId,
      name: color.name,
      swatchSpriteUrl: swatchSpriteUrls[swatchImage.swatchSpriteUrlIndex],
      swatchSpriteOffset: swatchImage.swatchSpriteOffset,
    };
  });
  return dropdowncolor;
}

function getAvailability(product, colorId, sizeId, typeId) {
  if (product.detail.flags.masterProduct) {
    return {};
  }
  return getAvailabilityForMember(product, colorId, sizeId, typeId);
}

// TBR
function isMasterOrMember(options) {
  return andFunctor(options.isMemberCard)(options.isMaster);
}

function parseSizes(product, availability) {
  if (!product.traits.sizes) {
    return null;
  }

  // eslint-disable-next-line prefer-destructuring
  const sizes = product.traits.sizes;
  const { sizeMap } = sizes;
  const firstKey = Object.keys(sizeMap)[0];
  const swatchSize = sizeMap[firstKey].displayName.length > 5 && !product.detail.flags.consolidatedProductComplex ? 'large' : '';
  let selectedSwatchIndex;
  const hasSelectedSize = _.isNumber(sizes.selectedSize);

  const swatches = sizes.orderedSizesBySeqNumber.map((sizeId, index) => {
    let typeId;
    const size = sizeMap[sizeId];
    selectedSwatchIndex = hasSelectedSize && sizeId === sizes.selectedSize ? index : selectedSwatchIndex;
    if (size.typeToColors) {
      typeId = parseInt(Object.keys(size.typeToColors)[0], 10);
    }
    return {
      id: sizeId,
      availableColors: size.colors,
      isAvailable: availability.sizes[sizeId],
      displayName: size.displayName,
      isSelected: selectedSwatchIndex === index,
      type: typeId,
      swatchSize,
    };
  });

  const { categories } = product.relationships.taxonomy;
  const typeName = product.detail.typeName;
  const isTwoRowsOnlyEnabled = checkCategoryEligibility(typeName, categories) && Properties.get('isTwoSizeRowEnabledKS');

  return {
    isTwoRowsOnlyEnabled,
    selectedSwatchIndex,
    selectedTierIndex: 0,
    swatchTiers: [{ swatches }],
  };
}

function parseTypes(product, availability) {
  const { detail, traits } = product;
  if (!traits.types) {
    return null;
  }
  const selectedType = traits.types.selectedType || Object.keys(traits.types.typeMap)[0];
  let selectedSwatchIndex;
  if (!detail.flags.bigTicketItem) {
    selectedSwatchIndex = Object.keys(product.traits.types.typeMap).indexOf(selectedType);
  }
  return {
    selectedSwatchIndex,
    selectedTierIndex: 0,
    typeText: getProperty(traits, 'types.typeText', 'Type'),
    swatchTiers: [{
      swatches: _.map(traits.types.typeMap, type => ({
        id: type.typeId,
        isAvailable: availability.types[type.typeId],
        displayName: type.typeName,
        isSelected: !detail.flags.bigTicketItem && type.typeId === parseInt(selectedType, 10),
      })),
    }],
  };
}

function buildColorData(product, availability) {
  const priceToColors = getProperty(product, 'traits.traitsMaps.priceToColors');
  const colors = getProperty(product, 'traits.colors', {});
  const { colorMap } = colors;
  const { selectedColor } = colors;
  const { orderedColorsById } = colors;
  const swatchSpriteUrls = getProperty(colors, 'swatchSprite.swatchSpriteUrls', {});
  const colorSwatches = [];
  const color = {
    swatchTiers: colorSwatches,
  };
  // this has to run for all elements as we are transforming data
  if (priceToColors) {
    priceToColors.forEach((priceToColor, tierIndex) => {
      const swatches = priceToColor.colorIds.map((colorId, swatchIndex) => {
        const swatchImage = colorMap[colorId].swatchImage || {};
        const firstBigTicketUpcInfo = (product.detail.flags.bigTicketItem) ? getFirstUpcByColorID(product, colorId) : {};
        const colorRefObj = {
          id: colorId,
          isAvailable: availability.colors[colorId],
          swatchSpriteOffset: swatchImage.swatchSpriteOffset,
          swatchSpriteUrl: swatchSpriteUrls[swatchImage.swatchSpriteUrlIndex],
          upcID: firstBigTicketUpcInfo.upcID,
          upcNumber: firstBigTicketUpcInfo.upcNumber,
        };
        if (colorId === selectedColor) {
          colorRefObj.isSelected = true;
          color.selectedSwatchIndex = swatchIndex;
          color.selectedTierIndex = tierIndex;
        }
        return colorRefObj;
      });
      const colorSwatch = {
        price: priceToColor.price,
        onSale: priceToColor.onSale,
        swatches,
      };
      colorSwatches.push(colorSwatch);
    });
  } else if (orderedColorsById) {
    const swatches = orderedColorsById.map((colorId, swatchIndex) => {
      const { swatchImage } = colorMap[colorId];
      const firstBigTicketUpcInfo = (product.detail.flags.bigTicketItem) ? getFirstUpcByColorID(product, colorId) : {};
      if (swatchImage) {
        const colorRefObj = {
          id: colorId,
          isAvailable: true,
          swatchSpriteOffset: swatchImage.swatchSpriteOffset,
          swatchSpriteUrl: swatchSpriteUrls[swatchImage.swatchSpriteUrlIndex],
          upcID: firstBigTicketUpcInfo.upcID,
          upcNumber: firstBigTicketUpcInfo.upcNumber,
        };
        if (colorId === selectedColor) {
          colorRefObj.isSelected = true;
          color.selectedSwatchIndex = swatchIndex;
          color.selectedTierIndex = 0;
        }
        return colorRefObj;
      }
      return undefined;
    });
    const colorSwatch = {
      swatches,
    };
    colorSwatches.push(colorSwatch);
  }
  return color;
}

function removePriceLabelInTieredPrice(pricing) {
  if (pricing && pricing.price) {
    _.each(pricing.price.tieredPrice, (labels) => {
      if (!(labels.label === undefined)) {
        labels.label = labels.label.replace('[PRICE]', '').trim();
      }

      // creating new placeholders to determine the type to publish on template.
      _.each(labels.values, (valueArray) => {
        if (valueArray.type === 'regular') {
          valueArray.regular = true;
        } else if (valueArray.type === 'discount') {
          valueArray.discount = true;
        }
      });
    });
  }

  return pricing;
}

function getAllTraits(viewState) {
  const viewStateDetail = viewState.detail;
  const traits = [];

  if (viewStateDetail.flags.hasColors) {
    traits.push('color');
  }

  if (viewStateDetail.flags.hasSizes) {
    traits.push('size');
  }

  if (viewStateDetail.flags.hasTypes) {
    traits.push('type');
  }

  return traits;
}

function getUnselectedTraits(viewState) {
  const viewStateDetail = viewState.detail;
  const unselectedTraits = [];
  if (viewStateDetail.flags.hasSizes && viewStateDetail.selectedSize === undefined) {
    unselectedTraits.push('size');
  }

  if (viewStateDetail.flags.hasTypes && viewStateDetail.selectedType === undefined) {
    unselectedTraits.push('type');
  }

  return unselectedTraits;
}

function getIsUpcAvailableInStore(viewStateDetail, productUpcs) {
  let result = false;

  if (viewStateDetail.selectedUpc) {
    const upc = productUpcs[viewStateDetail.selectedUpc];
    result = upc && upc.availability && (upc.availability.bopsAvailability || upc.availability.bossAvailability);
  }

  return result;
}

function getMasterGroupList(product, selectedLatchKeyIndex) {
  return product.detail.orderedMasterGroupList.map((name, index) => ({
    name,
    isSelected: index === selectedLatchKeyIndex,
  }));
}

function getBreadCrumbCategories(product) {
  let breadCrumbCategories = '';
  const categories = product.relationships && product.relationships.taxonomy
    && product.relationships.taxonomy.categories
    && product.relationships.taxonomy.categories.map(category => category.name);
  if (categories) {
    breadCrumbCategories = categories.join(' - ');
  }
  return breadCrumbCategories;
}

function getSimilarItemsUrl(product) {
  const relTaxonomyCategories = getProperty(product, 'relationships.taxonomy.categories', []);
  const defaultCategoryId = getProperty(product, 'relationships.taxonomy.defaultCategoryId', null);

  let similarItemsUrl = '';

  if (defaultCategoryId && relTaxonomyCategories.length > 0) {
    const defaultCategory = relTaxonomyCategories.filter(category => category.id === defaultCategoryId).shift();
    if (defaultCategory) {
      similarItemsUrl = getProperty(defaultCategory, 'url', '');
    }
  }
  return similarItemsUrl;
}

function getPickupDetails(upcs, viewStateDetail) {
  return upcs[viewStateDetail.selectedUpc].availability;
}

function shouldShowSponsoredItems(isInternationalMode, isRegistryMode, flags, isLuxury) {
  return Properties.get('pdpSponsoredItemsContentEnabled') && !isInternationalMode && !isRegistryMode && !isLuxury && !flags.finishLine;
}

function beautyCuralateFlagCheck(product, isLuxury) {
  return (Properties.get('beautyCuralateEnabled') && (product.detail.flags.beauty) && !isLuxury &&
  (!product.detail.flags.masterProduct));
}

function shouldDisplayShopByDept(isInternationalMode, isRegistryMode) {
  return !isInternationalMode && !isRegistryMode;
}

function getTotalReviewCount(product) {
  // don't add review details for collection child products
  const revSelectProductId = getProperty(product, 'detail.reviewStatistics.aggregate.productId', product.id);
  return getProperty(
    product,
    `review.includes.products[${revSelectProductId}].reviewStatistics.totalReviewCount`, 0,
  );
}

function selectTypes(types) {
  if (types.selectedType) return parseInt(types.selectedType, 10);
  if (types.typeMap) {
    return parseInt(Object.keys(types.typeMap)[0], 10);
  }
  return 0;
}

function buildViewState(product, options = {}) {
  const { traits, relationships } = product;
  const badgeIds = (!(product.pricing === undefined)) ? product.pricing.badgeIds : [];
  const isInternationalMode = isIshipPage(options);
  const isRegistryMode = isOnRegistryMode(product, options);
  const { traitsMaps } = traits;
  const selectedSize = traits.sizes && (traits.sizes.orderedSizesBySeqNumber
    && traits.sizes.orderedSizesBySeqNumber.length === 1 ? traits.sizes.orderedSizesBySeqNumber[0] : traits.sizes.selectedSize);
  const selectedSizeName = traits.sizes && getProperty(traits.sizes.sizeMap[selectedSize], 'displayName', '');

  const isBigTicketItem = getProperty(product, 'detail.flags.bigTicketItem', false);
  const isTypesAvailable = !isBigTicketItem && product.traits.types;
  const selectedType = isTypesAvailable ? selectTypes(product.traits.types) : undefined;

  const availability = getAvailability(product, traits.colors.selectedColor, selectedSize, selectedType);
  const { flags } = product.detail;
  const isMaster = flags.masterProduct;
  const isLuxury = flags.chanel || flags.hermes;
  const { isWebCollageOutFromTabs } = flags;
  const selectedUpc = isMaster ? undefined : getUpcId(product, traits.colors.selectedColor, selectedSize, selectedType);
  const selectedUpcItem = selectedUpc && relationships.upcs[selectedUpc];
  const colorMap = getProperty(product, 'traits.colors.colorMap', {});
  const traitsSelectedColor = getProperty(traits, 'colors.selectedColor');
  const selectedFinalPrice = isMaster ? {} : getProperty(colorMap[traitsSelectedColor], 'pricing.price.finalPrice', {});
  const consolidatedProductComplex = getProperty(product, 'detail.flags.consolidatedProductComplex', false);
  const colorBadges = andFunctor(consolidatedProductComplex)(selectedUpc) ? getProperty(relationships.upcs[selectedUpc], 'pricing.badgeIds', []) : getProperty(colorMap[traitsSelectedColor], 'pricing.badgeIds', []);
  const finalPrice = !_.isEmpty(selectedFinalPrice) ? formatFinalPrice(selectedFinalPrice) : {};
  const badgeData = getBadgeInformation(product);
  const showFinalPrice = !_.isEmpty(finalPrice);
  const ccfpObject = options.ccfp ? options.ccfp : product.ccfp;
  // change isGiftWithPurchaseItem to true to see the GiftPurchaseProductView for gifts
  const isGiftWithPurchaseItem = flags.gwpExperienceEnabled;
  const topBadges = getTopBadges(badgeData, finalPrice, showFinalPrice, flags, isGiftWithPurchaseItem, colorBadges);
  const similarItemsUrl = getSimilarItemsUrl(product);
  const selectedExperiments = options.experimentIds || [];
  const desktop = getDeviceType(options, 'DESKTOP');
  const phone = getDeviceType(options, 'PHONE');
  const tablet = getDeviceType(options, 'TABLET');
  const reviewBVAPISolutionEnabled = Properties.get('reviewBVAPISolutionEnabled');
  const isReviewResponsiveEnabled = Properties.get('isReviewResponsiveEnabled');
  const showDesktopBVApiExpArmOne = reviewBVAPISolutionEnabled && flags.isBVApiExpArmOneEnabled;
  const showDesktopBVApiExpArmTwo = reviewBVAPISolutionEnabled && flags.isBVApiExpArmTwoEnabled;
  const showMobileBVApiExpArmOne = isReviewResponsiveEnabled && flags.isBVApiExpArmOneEnabled;
  const showMobileBVApiExpArmTwo = isReviewResponsiveEnabled && flags.isBVApiExpArmTwoEnabled;
  const pbsOrderedSiblingsPresent = !(product.relationships.orderedSiblingProductIds === undefined);
  const orderedSiblingProductIds = pbsOrderedSiblingsPresent ? (product.relationships.orderedSiblingProductIds.length >= 1) : false;
  const displayProcessedDesc = getProperty(product, 'detail.flags.processedProdDesc', false);


  // calculate values for video over rails.
  const isVideoImageRailEnabled = getProperty(product, 'detail.flags.isVideoImageRailEnabled', false);
  const imagery = andFunctor(consolidatedProductComplex)(selectedUpc) ? getProperty(relationships, `upcs[${selectedUpc}].imagery`) : getProperty(product, 'imagery', { videoId: false, images: [] });
  const { videoId } = imagery;
  const imagesInimagery = getProperty(imagery, 'images.length', 0);
  const videoImageRailTrueFlag = andFunctor(isVideoImageRailEnabled)(videoId);
  imagery.itemQty = videoImageRailTrueFlag ? imagesInimagery + 1 : imagesInimagery;
  imagery.hasImagesRail = videoImageRailTrueFlag ? imagesInimagery > 0 : imagesInimagery > 1;
  imagery.applyFixForManyAltImagesMobile = imagery.itemQty > 13;

  // Add a fake image to implement the video over rails.
  if (videoImageRailTrueFlag) {
    const imageArray = imagery.images;
    imagery.images.splice(3, 0, imagesAdapter({ videoId }, imageArray));
  }
  const viewState = {
    imagery,
    detail: {
      flags: {
        isWebCollageOutFromTabs,
        isLuxury,
        isInternationalMode,
        isRegistryMode,
        hasColors: flags.hasColors,
        hasColorSwatches: flags.hasColorSwatches,
        hasSizes: !!traits.sizes,
        hasTypes: !!traits.types,
        isGiftWithPurchaseItem,
        isVideoImageRailEnabled: videoImageRailTrueFlag,
        shouldDisplayAddToList: !isInternationalMode,
        shouldDisplayAddToRegistry: !isInternationalMode && product.detail.flags.registrable,
        shouldDisplayBigTicketAvailabilityMessage: (Properties.get('bigTicketV2CEnabled') && !product.detail.flags.bigTicketV2CItem) || !Properties.get('bigTicketV2CEnabled'),
        shouldDisplayMasterGrid: Properties.get('isMasterGridEnabled') && flags.memberProduct && !isLuxury && !options.isMemberCard,
        isMultiTiered: flags.hasColorSwatches && traitsMaps.priceToColors && traitsMaps.priceToColors.length > 1,
        isMemberCard: options.isMemberCard,
        shouldDisplayColorPicker: !flags.masterProduct && flags.beauty,
        storeOnlySpecial: flags.storeOnlySpecial,
        shouldDisplayReviewSummary: !!(!isInternationalMode || (product.detail.reviewStatistics && product.detail.reviewStatistics.aggregate)),
        displaySponsoredItems: shouldShowSponsoredItems(isInternationalMode, isRegistryMode, flags, isLuxury),
        enableAdobeImpressionTagsOnSponsoredItems: (shouldShowSponsoredItems(isInternationalMode, isRegistryMode, flags, isLuxury) && !phone && isServerSideExperimentEnabled('567-21', options)),
        displaySponsoredItemsTop: (shouldShowSponsoredItems(isInternationalMode, isRegistryMode, flags, isLuxury) && Properties.get('pdpAboveSponsoredItemsContentEnabled') && !phone),
        displaySponsoredItemsRedesign: shouldShowSponsoredItems(isInternationalMode, isRegistryMode, flags, isLuxury) && Properties.get('pdpSponsoredItemsContentEnabledServerSide'),
        isTrueFitEligible: (Properties.get('isTrueFitModalEnabled') && flags.truefitEligible && !isMaster && traits.sizes && !options.isMemberCard),
        isbeautyCuralate: beautyCuralateFlagCheck(product, isLuxury),
        isFindationMatch: flags.isFindationEnabled && !isLuxury && !(flags.masterProduct),
        sizePersistForMen: getProperty(product, 'detail.flags.sizePersistForMen', false),
        displayShopByDept: shouldDisplayShopByDept(isInternationalMode, isRegistryMode),
        showSizesDropdownForShoes: (
          flags.sizesDropdownForShoesEnabled &&
          selectedExperiments.includes('309-21') &&
          !isMasterOrMember(Object.assign({ isMaster }, options))
        ),
        isTabsRedesignArmOne: Properties.get('isTabsRedesignEnabled') && isServerSideExperimentEnabled('362-21', options),
        isTabsRedesignArmTwo: Properties.get('isTabsRedesignEnabled') && isServerSideExperimentEnabled('362-22', options),
        showDesktopBVApiExpArmOne,
        showDesktopBVApiExpArmTwo,
        showMobileBVApiExpArmOne,
        showMobileBVApiExpArmTwo,
        showBVApiExp: (
          (isReviewResponsiveEnabled || reviewBVAPISolutionEnabled) &&
          (
            (desktop && (showDesktopBVApiExpArmOne || showDesktopBVApiExpArmTwo)) ||
            ((phone || tablet) && (showMobileBVApiExpArmOne || showMobileBVApiExpArmTwo))
          )
        ),
        orderedSiblingProductIds,
        consolidatedProductComplex,
        prosStitchesWithWeights: flags.prosStitchesWithWeights,
        prosStitchesWithoutWeights: flags.prosStitchesWithoutWeights,
        prosStitchesControl: flags.prosStitchesControl,
        isSameDayDeliveryEnabled: Properties.get('enhanceSameDayDeliveryEnabled') && !flags.bigTicketItem && !isRegistryMode && !isMaster,
        isBigTicketItem,
        displayProcessedDesc,
        isFewLeftMessageRedesignEnabled: flags.isFewLeftMessageRedesignEnabled,
      },
      selectedQuantity: 1,
      isMaximumQuantity: false,
      isMinimumQuantity: true,
      selectedUpc,
      selectedUpcItem,
      colorMap,
      colorBadges,
      selectedColor: traits.colors.selectedColor,
      selectedColorName: getProperty(colorMap[traits.colors.selectedColor], 'name'),
      selectedType,
      selectedSize,
      selectedSizeName,
      selectedPrice: removePriceLabelInTieredPrice(consolidatedProductComplex && selectedUpc ?
        getProperty(relationships, `upcs[${selectedUpc}].pricing`, null) :
        getProperty(colorMap[traitsSelectedColor], 'pricing', null)),
      selectedFinalPrice: (!(finalPrice === undefined) && showFinalPrice) ? getFinalPrice(topBadges, finalPrice) : {},
      interactedAltImages: 0,
      selectedMemberDisplayGroup: isMaster ? product.detail.orderedMasterGroupList[0] : undefined,
      isShipItSelected: true,
      isPickUpInStoreSelected: false,
      breadCrumbCategory: getBreadCrumbCategories(product),
      similarItemsUrl,
      ...((flags.hasColors && !flags.masterProduct && flags.beauty) && { colorPickerDropdown: buildDropDownColorData(product) }),
      tealiumInteractedAttributeCount: 0,
    },
    relationships: {
      masterProduct: {
        id: options.masterProductId,
      },
    },
    traits: {
      size: parseSizes(
        product,
        availability,
        options,
      ),
      type: parseTypes(product, availability),
      color: flags.hasColorSwatches && buildColorData(product, availability),
    },
    ccfp: ccfpObject,
    badges: {
      badgeData,
      topBadges,
      shouldDisplayMoreOffers: !(badgeIds === undefined) && badgeIds.length > 2,
    },
    availability: product.availability,
    featuresViewState: {
      accordionPriceDetail: true,
      socialIcons: true,
      egiftCardEnabled: false,
    },
  };

  if (product.review) {
    const totalReviewCount = getTotalReviewCount(product);

    const isSeoAllReviewsPageLinkEnabled = Properties.get('seoProductReviewsPageEnabled') &&
      isServerSideExperimentEnabled('441-21', options) &&
      totalReviewCount > SEO_SHOW_ALL_REVIEWS_OFFSET &&
      getProperty(product, 'detail.reviewStatistics.aggregate.productId', product.id) === product.id;

    Object.assign(viewState.detail.flags, {
      isSeoAllReviewsPageLinkEnabled,
      isSeoSeeAllReviewsLinkClickAnalyticsEnabled: Properties.get('seoSeeAllReviewsLinkClickAnalyticsEnabled'),
    });

    Object.assign(viewState, {
      reviews: {
        totalReviewCount,
      },
    });
  }

  if (!isMaster) {
    Object.assign(viewState.detail, {
      unselectedTraits: getUnselectedTraits(viewState).join('/'),
      isUpcAvailableInStore: getIsUpcAvailableInStore(viewState.detail, product.relationships.upcs),
    });
    if (viewState.detail.isUpcAvailableInStore) {
      viewState.detail.pickupDetails = getPickupDetails(product.relationships.upcs, viewState.detail);
    }
  } else {
    const hasLatchKeys = product.detail.orderedMasterGroupList.length > 1;

    if (hasLatchKeys) {
      const selectedLatchKeyIndex = 0;
      Object.assign(viewState.detail, {
        hasLatchKeys,
        selectedLatchKeyIndex,
        orderedMasterGroupList: getMasterGroupList(product, selectedLatchKeyIndex),
      });
    }
  }
  if (product.detail.flags.consolidatedProductComplex && selectedUpc) {
    const upcProductDetail = getProperty(product, `relationships.upcs[${selectedUpc}].detail`);
    Object.assign(viewState.detail, {
      description: upcProductDetail.description,
      bulletText: upcProductDetail.bulletText,
      bulletLinks: upcProductDetail.bulletLinks,
      name: upcProductDetail.name,
      memberShortName: upcProductDetail.memberShortName,
    });
  }
  return viewState;
}

function buildMemberItem(params = {}) {
  const product = params.memberProductIds[params.productId];
  let { index } = params;
  index += 1;
  params.options.isMemberCard = true;
  return {
    product,
    viewState: buildViewState(product, params.options),
    properties: params.properties,
    index,
  };
}

const memberItemsMap = new Map();

function getMemberItem(params = {}) {
  let memberItem;

  if (memberItemsMap.has(params.productId)) {
    memberItem = memberItemsMap.get(params.productId);
  } else {
    memberItem = buildMemberItem(params);
    memberItemsMap.set(params.productId, memberItem);
  }

  return memberItem;
}

function buildMemberItemsObject(params) {
  const isFirstPage = params.isFirstPage === undefined ? true : params.isFirstPage;
  const {
    pageSize = 10,
    options = {},
    properties = {},
    latchKeyIndex = 0,
  } = params;
  const { detail } = params.product;

  const selectedLatchKeyName = detail.orderedMasterGroupList[latchKeyIndex];
  const memberProductIds = params.product.relationships.memberProductMap;

  let selectedLatchKeyProducts = detail.memberDisplayGroupsMap[selectedLatchKeyName];
  let showViewMoreMemberItems = false;
  let viewMoreMemberItemsCount = 0;

  // If only 1 latch key, then check for view more
  if (detail.orderedMasterGroupList.length === 1) {
    const initialPos = isFirstPage ? 0 : pageSize;
    const finalPos = isFirstPage ? pageSize : undefined;

    showViewMoreMemberItems = selectedLatchKeyProducts.length > 0 ? (isFirstPage && selectedLatchKeyProducts.length > pageSize) : false;
    viewMoreMemberItemsCount = selectedLatchKeyProducts.length - pageSize;

    selectedLatchKeyProducts = selectedLatchKeyProducts.slice(initialPos, finalPos);
  }

  options.masterProductId = params.product.id;
  options.ccfp = params.product.ccfp;

  const memberItems = selectedLatchKeyProducts ? selectedLatchKeyProducts.map((latchKey, index) => getMemberItem({
      memberProductIds,
      options,
      productId: latchKey,
      properties,
      index,
    })) : {};

  return {
    memberItems,
    showViewMoreMemberItems,
    viewMoreMemberItemsCount,
  };
}

function attributeNumToString(number) {
  let wordNum = '';
  switch (number) {
    case (1): wordNum = 'one'; break;
    case (2): wordNum = 'two'; break;
    case (3): wordNum = 'three'; break;
    default: wordNum = 'zero'; break;
  }
  return wordNum;
}

function numOfAttributes(viewState) {
  let attributeCount = 0;

  if (viewState.detail.flags.hasColorSwatches) {
    attributeCount += 1;
  }
  if (viewState.detail.flags.hasSizes) {
    attributeCount += 1;
    if (viewState.traits.size.hasWidth || viewState.traits.size.hasDressSplit) {
      attributeCount += 1;
    }
  }
  if (viewState.detail.flags.hasTypes) {
    attributeCount += 1;
  }
  return attributeNumToString(attributeCount);
}

function parseResponse(response, options = {}) {
  if (response.product[0]) {
    const product = response.product[0];
    const {flags} = product.detail;
    const viewState = buildViewState(product, options);
    const registryModeCanonical = getProperty(response, 'meta.tags.canonical', false);

    let memberProducts;
    let utagData;

    if (response.meta && response.meta.analytics) {
      utagData = Object.assign({
        is_responsive_pdp: 'true',
        eligible_attribute_count: numOfAttributes(viewState), // TO BE CONFIRMED - SELECTORS
      }, response.meta.analytics.data);
    }

    if (flags.masterProduct) {
      memberItemsMap.clear();

      const memberItemsObject = buildMemberItemsObject({
        product,
        options,
        properties: response.properties,
      });

      memberProducts = memberItemsObject.memberItems;
      viewState.detail.showViewMoreMemberItems = memberItemsObject.showViewMoreMemberItems;
      viewState.detail.viewMoreMemberItemsCount = memberItemsObject.viewMoreMemberItemsCount;
    }
    return {
      utagData,
      product,
      viewState,
      registryModeCanonical,
      memberProducts,
      context: options.context || {},
    };
  } else {

    return {

    };
  }



}

function buildTieredPriceForReviews(pricing) {
  let tieredPrice = getProperty(removePriceLabelInTieredPrice(pricing), 'price.tieredPrice');
  const giftPriceLabelToAllUpcs = getProperty(pricing, 'price.priceType.giftPriceLabelToAllUpcs');
  const onSale = getProperty(pricing, 'price.priceType.onSale');
  const applicableToAllUpcs = getProperty(pricing, 'price.priceType.applicableToAllUpcs');

  /* If gift price label is available then remove the first value */
  /* The first value for that product is the gift price label */
  if (giftPriceLabelToAllUpcs) {
    tieredPrice.shift();
  }
  /* Display only regular price when onSale or applicableToAllUpcs is false */
  if (!(onSale && applicableToAllUpcs)) {
    tieredPrice = [tieredPrice[0]];
  }
  return tieredPrice;
}

/** View State for reviews page
 *
 * @param product
 * @param options
 * @returns {Object}
 */
function buildReviewViewState(response, options = {}) {
  const product = response.product[0];
  const reviewsPageDefaults = response.reviewsPageDefaults;
  const { flags } = product.detail;
  const isLuxury = flags.chanel || flags.hermes;
  const desktop = getDeviceType(options, 'DESKTOP');
  const phone = getDeviceType(options, 'PHONE');
  const tablet = getDeviceType(options, 'TABLET');
  const reviewBVAPISolutionEnabled = Properties.get('reviewBVAPISolutionEnabled');
  const isReviewResponsiveEnabled = Properties.get('isReviewResponsiveEnabled');
  const showDesktopBVApiExpArmOne = reviewBVAPISolutionEnabled && flags.isBVApiExpArmOneEnabled;
  const showDesktopBVApiExpArmTwo = reviewBVAPISolutionEnabled && flags.isBVApiExpArmTwoEnabled;
  const showMobileBVApiExpArmOne = isReviewResponsiveEnabled && flags.isBVApiExpArmOneEnabled;
  const showMobileBVApiExpArmTwo = isReviewResponsiveEnabled && flags.isBVApiExpArmTwoEnabled;
  const totalReviewCount = getTotalReviewCount(product);
  const viewState = {
    detail: {
      flags: {
        isLuxury,
        showDesktopBVApiExpArmOne,
        showDesktopBVApiExpArmTwo,
        showMobileBVApiExpArmOne,
        showMobileBVApiExpArmTwo,
        showBVApiExp: (
          (isReviewResponsiveEnabled || reviewBVAPISolutionEnabled) &&
          (
            (desktop && (showDesktopBVApiExpArmOne || showDesktopBVApiExpArmTwo)) ||
            ((phone || tablet) && (showMobileBVApiExpArmOne || showMobileBVApiExpArmTwo))
          )
        ),
        isReviewsPage: flags.isReviewsPage,
      },
      tieredPrice: !flags.masterProduct && buildTieredPriceForReviews(getProperty(product, 'pricing')),
    },
    reviewsPageDefaults,
    reviewsResultsRange: {
      firstEntry: reviewsPageDefaults.offset + 1,
      lastEntry: Math.min((reviewsPageDefaults.offset + reviewsPageDefaults.limit), totalReviewCount),
    },
  };

  return viewState;
}

function parseReviewsPageResponse(response, options = {}) {
  const product = response.product[0];
  const viewState = buildReviewViewState(response, options);

  const utagData = getProperty(response, 'meta.analytics.data', {});
  const metaTags = getProperty(response, 'meta.tags', {});

  const { pagination } = response;

  return {
    utagData,
    pagination,
    product,
    viewState,
    metaTags,
    context: options.context || {},
  };
}

/*
 * Returns total member items count from the given master product model with given size index
 * @param productData - product node from product model
 * @param sizeIdx - index of size selected
 */
const getMemberItemsCountBySizeIndex = (productData, sizeIdx = 0) => {
  const {
    detail: {
      memberDisplayGroupsMap,
      orderedMasterGroupList,
    },
  } = productData;
  const sizeLabel = orderedMasterGroupList && orderedMasterGroupList[sizeIdx];
  const resultProductIds = (memberDisplayGroupsMap && memberDisplayGroupsMap[sizeLabel]) || [];
  return resultProductIds.length;
};

// TODO: use `export function parseMethod` instead of returning this object.
//      not currently supported due to low node version used in backend
module.exports = {
  parseResponse,
  parseReviewsPageResponse,
  getAvailability,
  getAvailabilityForMember,
  removePriceLabelInTieredPrice,
  getAllTraits,
  getUnselectedTraits,
  getIsUpcAvailableInStore,
  getBreadCrumbCategories,
  buildMemberItemsObject,
  getMasterGroupList,
  formatFinalPrice,
  getBadgeInformation,
  getTopBadges,
  getFinalPrice,
  getPickupDetails,
  isRegistryUrlMode,
  getMemberItem,
  getFirstUpcByColorID,
  getMemberItemsCountBySizeIndex,
  getSizesByType,
  SizesType,
  buildTieredPriceForReviews,
};



// WEBPACK FOOTER //
// /Users/m802941/WebstormProjects/2020/campaigns/shop-the-look-pdp/node_modules/@page/digital-product-ui-mcom/src/js/util/ProductModelParser.js